import { v4 as uuidv4 } from 'uuid';

const asArray = (value) => (Array.isArray(value) ? value : []);

function firstContactAddress(entries) {
  return asArray(entries).find((entry) => entry?.contact_address)?.contact_address || ''
}

function allowedId(options, key, candidate) {
  if (candidate === undefined || candidate === null || candidate === '') return '';
  const match = asArray(options).find((option) => String(option?.[key]) === String(candidate));
  return match?.[key] ?? '';
}

function adaptContact(source, dropdowns) {
  const attrs = source?.data?.attributes || source || {}
  const contactTypeCandidate = attrs.contact_type_id ?? attrs.contactType?.contact_type_id
  const statusCandidate =
    attrs.contact_status_type_id ??
    attrs.statusType?.status_type_id ??
    attrs.contactStatusType?.status_type_id
  return {
    id: uuidv4(),
    contact_first_name: attrs.contact_first_name ?? attrs.first_name ?? '',
    contact_last_name: attrs.contact_last_name ?? attrs.last_name ?? '',
    contact_type_id: allowedId(dropdowns.contactTypes, 'contact_type_id', contactTypeCandidate),
    contact_status_type_id: allowedId(
      dropdowns.contactStatusOptions?.length ? dropdowns.contactStatusOptions : dropdowns.entityStatusOptions,
      'status_type_id',
      statusCandidate,
    ),
    email: String(attrs.email || firstContactAddress(attrs.emailAddresses)).trim().toLowerCase(),
    phone: attrs.phone || firstContactAddress(attrs.phoneAddresses),
    title: attrs.title || '',
    createUserAccount: false,
  }
}

function adaptAddress(source, dropdowns) {
  return {
    id: uuidv4(),
    address_type_id: allowedId(dropdowns.addressTypes, 'address_type_id', source?.address_type_id),
    name: source?.name || '',
    street_address1: source?.street_address1 || '',
    street_address2: source?.street_address2 || '',
    city: source?.city || '',
    state: source?.state || '',
    postal_code: source?.postal_code || '',
    country: source?.country || '',
    latitude: '',
    longitude: '',
    status_type_id: allowedId(
      dropdowns.addressStatusOptions?.length ? dropdowns.addressStatusOptions : dropdowns.entityStatusOptions,
      'status_type_id',
      source?.status_type_id,
    ),
  };
}

export function adaptIntakeDraft(response, dropdowns) {
  const normalized = response?.normalized || {};
  // Backend may return entities_payload either wrapped in data.attributes or flat.
  const attrs =
    normalized?.entities_payload?.data?.attributes ||
    normalized?.entities_payload ||
    {};
  let sourceAddresses = asArray(attrs.addresses);
  // Support legacy payload where address is a plain string
  if (!sourceAddresses.length && typeof attrs.address === 'string' && attrs.address.trim()) {
    const addrStr = attrs.address.trim();
    sourceAddresses = [
      {
        id: uuidv4(),
        name: addrStr,
        street_address1: addrStr,
      },
    ];
  }
  const contacts = [];
  if (attrs.contact) contacts.push(adaptContact(attrs.contact, dropdowns));
  asArray(normalized.secondary_contacts_payload).forEach((c) => contacts.push(adaptContact(c, dropdowns)));
  return {
    requestId: response?.request_id || '',
    confidence: Number(response?.confidence) || 0,
    warnings: asArray(response?.warnings),
    missingRequiredFields: asArray(response?.missing_required_fields),
    entity: {
      // Prefer explicit fields, fall back to generic name handling
      name: attrs.name || attrs.entity_name || '',
      first_name: attrs.first_name || attrs.firstName || '',
      last_name: attrs.last_name || attrs.lastName || '',
      company_id: allowedId(dropdowns.companies, 'company_id', attrs.company?.company_id),
      entity_type_id: allowedId(dropdowns.entityTypes, 'entity_type_id', attrs.entityType?.entity_type_id),
      status_type_id: allowedId(dropdowns.entityStatusOptions, 'status_type_id', attrs.statusType?.status_type_id),
    },
    addresses: sourceAddresses.map((a) => adaptAddress(a, dropdowns)),
    contacts,
  };
}

function isBlankAddress(address) {
  return ![address?.name, address?.street_address1, address?.city, address?.state, address?.postal_code].some(Boolean);
}
function isBlankContact(contact) {
  return ![contact?.contact_first_name, contact?.contact_last_name, contact?.email, contact?.phone].some(Boolean);
}
function sameAddress(l, r) {
  const key = (a) => `${a.street_address1?.trim()?.toLowerCase()||''}|${a.city?.trim()?.toLowerCase()||''}|${a.state?.trim()?.toLowerCase()||''}|${a.postal_code?.trim()?.toLowerCase()||''}`;
  return key(l) === key(r);
}
function sameContact(l, r) {
  const le = String(l.email || '').trim().toLowerCase();
  const re = String(r.email || '').trim().toLowerCase();
  if (le && re) return le === re;
  const lp = String(l.phone || '').replace(/\D/g, '');
  const rp = String(r.phone || '').replace(/\D/g, '');
  return Boolean(lp && rp && lp === rp);
}

export function mergeSelectedAiDraft(previous, draft, selection) {
  const entity = { ...previous.entity };
  Object.entries(selection.entity || {}).forEach(([field, selected]) => {
    if (selected && draft.entity?.[field] !== '') entity[field] = draft.entity[field];
  });
  const selectedAddresses = draft.addresses.filter((a) => selection.addresses?.[a.id]);
  const selectedContacts = draft.contacts.filter((c) => selection.contacts?.[c.id]);
  let addresses = [...previous.addresses];
  selectedAddresses.forEach((addr) => {
    if (addresses.some((cur) => sameAddress(cur, addr))) return;
    const blankIdx = addresses.findIndex(isBlankAddress);
    if (blankIdx >= 0) addresses[blankIdx] = addr; else addresses.push(addr);
  });
  let contacts = [...previous.contacts];
  selectedContacts.forEach((c) => {
    if (contacts.some((cur) => sameContact(cur, c))) return;
    const blankIdx = contacts.findIndex(isBlankContact);
    if (blankIdx >= 0) contacts[blankIdx] = c; else contacts.push(c);
  });
  return { ...previous, entity, addresses, contacts };
}
